import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Button from '../Button';
import Icon from '../Icon';
import LoadingIndicator from '../LoadingIndicator';
import { getQTipData } from '../Tooltip/qTip.utilities';
import type { IconType } from '../Icon/Icon.types';
import type { ButtonWithDropdownProps, DropdownItems } from './ButtonWithDropdown.types';

const borderStyles = [
  'tw:border-solid',
  'tw:border',
  'tw:rounded-[0.5rem]',
  'tw:border-sq-disabled-gray',
  'tw:dark:border-gray-500',
].join(' ');

const bgStyles = ['tw:bg-sq-white', 'tw:dark:bg-sq-dark-background'].join(' ');

const renderDropdownItemIcon = ({
  item,
  iconExtraClassNames,
  spinnerExtraClassNames = iconExtraClassNames,
  iconType = 'text',
}: {
  item: DropdownItems;
  iconExtraClassNames: string;
  spinnerExtraClassNames?: string;
  iconType?: IconType;
}) => {
  if (item.inProgress) {
    return (
      <LoadingIndicator
        size={16}
        color="currentColor"
        testId={item.iconTestId}
        customId={item.iconCustomId}
        extraClassNames={spinnerExtraClassNames}
      />
    );
  }

  if (!item.icon) {
    return null;
  }

  return (
    <Icon
      icon={item.icon}
      testId={item.iconTestId}
      type={iconType}
      color={item.iconColor}
      customId={item.iconCustomId}
      extraClassNames={iconExtraClassNames}
    />
  );
};

const ButtonWithDropdown = (props: ButtonWithDropdownProps) => {
  const {
    label,
    variant = 'outline',
    size = 'sm',
    icon,
    iconStyle = 'text',
    iconColor,
    iconPosition = 'left',
    iconPrefix,
    extraClassNames = '',
    testId,
    id,
    disabled = false,
    inProgress = false,
    ...tooltipProps
  } = props;

  return (
    <Dropdown {...props}>
      <DropdownMenu.Trigger asChild id={id} disabled={disabled}>
        <Button
          label={label}
          variant={variant}
          size={size}
          icon={icon}
          iconStyle={iconStyle}
          iconColor={iconColor}
          iconPosition={iconPosition}
          iconPrefix={iconPrefix}
          extraClassNames={extraClassNames}
          testId={testId}
          disabled={disabled}
          inProgress={inProgress}
          tooltip={tooltipProps.tooltip}
          tooltipOptions={{
            delay: tooltipProps.tooltipDelay,
            position: tooltipProps.tooltipPlacement,
          }}
        />
      </DropdownMenu.Trigger>
    </Dropdown>
  );
};

export const Dropdown = ({
  dropdownItems,
  id,
  children,
  contentExtraClassNames = '',
  containerTestId,
  align = 'end',
  placement = 'bottom',
  placementOffset = 5,
  alignOffset = -35,
  hasArrow = false,
  onOpenChange,
  isOpen,
  setFocusOnTriggerOnClose = true,
  keepFocusInsideDropdown = true,
  onContainerClick,
}: ButtonWithDropdownProps) => {
  return (
    <DropdownMenu.Root defaultOpen={false} open={isOpen} onOpenChange={onOpenChange} modal={keepFocusInsideDropdown}>
      {children}
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          onClick={onContainerClick}
          sideOffset={placementOffset}
          side={placement}
          align={align}
          alignOffset={alignOffset}
          asChild
          onCloseAutoFocus={(e) => !setFocusOnTriggerOnClose && e.preventDefault()}
          className="tw:focus-visible:outline-none tw:outline-none">
          <div
            data-testid={containerTestId}
            className={`${bgStyles} tw:relative tw:z-[1200] tw:min-w-6 tw:py-2 tw:focus-visible:outline-none tw:outline-none tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out forceFont tw:data-[side=top]:animate-slideDownAndFade tw:data-[side=right]:animate-slideLeftAndFade tw:data-[side=bottom]:animate-slideUpAndFade tw:data-[side=left]:animate-slideRightAndFade ${borderStyles} ${contentExtraClassNames}`}>
            {hasArrow && (
              <DropdownMenu.Arrow asChild>
                <div className="tw:fill-transparent tw:bg-sq-white tw:w-sq-15 tw:h-sq-15 tw:rotate-45 tw:dark:bg-sq-dark-background tw:border-b tw:border-r tw:border-sq-disabled-gray tw:dark:border-gray-500 tw:-mt-sq-7" />
              </DropdownMenu.Arrow>
            )}
            {dropdownItems.map((item, index) => {
              const tooltipData = getQTipData(item);

              if (item.isLabel) {
                return (
                  <DropdownMenu.Label
                    key={(item.id || '') + index}
                    className={item.itemExtraClassNames}
                    {...tooltipData}>
                    {(item.icon || item.inProgress) &&
                      renderDropdownItemIcon({
                        item,
                        iconExtraClassNames: 'tw:text-sq-text-color tw:dark:text-sq-white tw:w-4.5',
                        spinnerExtraClassNames: 'tw:text-sq-text-color tw:dark:text-sq-white',
                      })}
                    <div
                      data-testid={item.labelTestId}
                      className={`tw:text-sq-13 tw:ml-1 ${
                        item.disabled ? 'tw:text-sq-disabled-gray' : 'tw:text-sq-text-color tw:dark:text-sq-white'
                      } tw:not-italic tw:font-normal ${item.labelClasses}`}>
                      {item.label}
                    </div>
                  </DropdownMenu.Label>
                );
              }
              if (Array.isArray(item.subMenuItems) && item.subMenuItems.length > 0) {
                return (
                  <DropdownMenu.Sub key={(item.id || item.icon || '') + index}>
                    <DropdownMenu.SubTrigger
                      id={id}
                      className={`tw:cursor-pointer tw:flex tw:justify-between tw:dark:text-sq-white tw:hover:bg-sq-gray-highlight tw:dark:hover:bg-sq-gray-highlight-dark tw:leading-none tw:items-center tw:h-sq-27 tw:pl-sq-19 tw:relative tw:select-none tw:outline-none tw:data-disabled:pointer-events-none ${
                        item.itemExtraClassNames || ''
                      }`}
                      data-testid={item.testId}
                      disabled={item.disabled}
                      {...tooltipData}>
                      <div className="tw:flex">
                        {(item.icon || item.inProgress) && (
                          <div className="tw:min-w-5">
                            {renderDropdownItemIcon({
                              item,
                              iconExtraClassNames: `tw:w-4.5 ${
                                item.disabled
                                  ? 'tw:text-sq-disabled-gray!'
                                  : 'tw:text-sq-text-color tw:dark:text-sq-white'
                              } ${item.iconExtraClassNames || ''}`,
                              spinnerExtraClassNames: `${
                                item.disabled
                                  ? 'tw:text-sq-disabled-gray!'
                                  : 'tw:text-sq-text-color tw:dark:text-sq-white'
                              } ${item.iconExtraClassNames || ''}`,
                            })}
                          </div>
                        )}
                        <div
                          data-testid={item.labelTestId}
                          data-customid={item.labelCustomId}
                          className={`tw:text-sq-13 tw:ml-1 ${
                            item.disabled && 'tw:opacity-30!'
                          } tw:not-italic tw:font-semibold ${item.labelClasses}`}>
                          {item.label}
                        </div>
                      </div>
                      <div className="tw:flex tw:justify-center tw:items-center">
                        <Icon
                          size="sm"
                          icon="fc-arrow-dropdown tw:rotate-270"
                          extraClassNames={`${
                            item.disabled ? 'tw:text-sq-disabled-gray' : 'tw:text-sq-text-color tw:dark:text-sq-white'
                          } tw:ml-4 tw:mr-2 tw:text-2`}
                        />
                      </div>
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.SubContent className="tw:focus-visible:outline-none tw:outline-none">
                        <div
                          data-testid={containerTestId}
                          className={`${bgStyles} tw:relative tw:z-[1000] tw:min-w-6 tw:py-2 tw:focus-visible:outline-none tw:outline-none tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out forceFont tw:data-[side=top]:animate-slideDownAndFade tw:data-[side=right]:animate-slideLeftAndFade tw:data-[side=bottom]:animate-slideUpAndFade tw:data-[side=left]:animate-slideRightAndFade ${borderStyles}`}>
                          {item.subMenuItems.map((subItem, subIndex) => {
                            return (
                              <DropdownMenu.Item
                                key={subItem.label + subIndex}
                                onSelect={subItem.onClick}
                                data-qtip-text={subItem.tooltip}
                                className="tw:cursor-pointer tw:flex tw:dark:text-sq-white tw:hover:bg-sq-gray-highlight tw:dark:hover:bg-sq-gray-highlight-dark tw:leading-none tw:items-center tw:h-sq-27 tw:px-sq-19 tw:relative tw:select-none tw:outline-none tw:data-disabled:pointer-events-none"
                                disabled={subItem.disabled}>
                                {subItem.iconClass && (
                                  <Icon
                                    icon={subItem.iconClass}
                                    type="text"
                                    extraClassNames={`tw:w-4.5 ${item.disabled && 'tw:opacity-30!'}`}
                                  />
                                )}
                                <div
                                  className={`tw:text-sq-13 tw:ml-1 ${
                                    subItem.disabled
                                      ? 'tw:text-sq-disabled-gray'
                                      : 'tw:text-sq-text-color tw:dark:text-sq-white'
                                  } tw:not-italic tw:font-normal ${item.labelClasses}`}>
                                  {subItem.label}
                                </div>
                              </DropdownMenu.Item>
                            );
                          })}
                        </div>
                      </DropdownMenu.SubContent>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Sub>
                );
              }
              return (
                <div key={(item.id || '') + index} {...tooltipData}>
                  <DropdownMenu.Item
                    key={(item.id || item.icon || '') + index}
                    data-customid={item.itemCustomId}
                    onSelect={(e) => {
                      item.onClick(e);
                    }}
                    className={`tw:cursor-pointer tw:flex tw:dark:text-sq-white tw:hover:bg-sq-gray-highlight tw:dark:hover:bg-sq-gray-highlight-dark tw:leading-none tw:items-center tw:min-h-sq-27 tw:px-sq-19 tw:relative tw:select-none tw:outline-none tw:data-disabled:pointer-events-none ${
                      item.itemExtraClassNames || ''
                    }`}
                    data-testid={item.testId}
                    disabled={item.disabled}>
                    {(item.icon || item.inProgress) && (
                      <div className="tw:min-w-5">
                        {renderDropdownItemIcon({
                          item,
                          iconType: (item.iconType || 'text') as IconType,
                          iconExtraClassNames: `tw:w-sq-4.5 ${item.disabled && 'tw:opacity-30!'} ${
                            item.iconExtraClassNames || ''
                          }`,
                          spinnerExtraClassNames: `${item.disabled && 'tw:opacity-30!'} ${
                            item.iconExtraClassNames || ''
                          }`,
                        })}
                      </div>
                    )}
                    <div
                      data-testid={item.labelTestId}
                      data-customid={item.labelCustomId}
                      className={`tw:text-sq-13 tw:ml-1 ${
                        item.disabled ? 'tw:text-sq-disabled-gray' : 'tw:text-sq-text-color tw:dark:text-sq-white'
                      } tw:not-italic tw:font-normal ${item.labelClasses || ''}`}>
                      {item.label}
                    </div>
                  </DropdownMenu.Item>
                  {item.hasDivider && (
                    <DropdownMenu.Separator
                      data-testid={`dropdown-divider-${index}`}
                      className="tw:h-px tw:bg-sq-disabled-gray tw:dark:bg-gray-500 tw:my-2"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default ButtonWithDropdown;
